import * as Sentry from '@sentry/react'
import { QueryErrorResetBoundary } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router'
import { LOGOUT_PATH } from '../../constants'
import { ApiError } from '../../utils/api-client/api-error'
import { ZodParseError } from '../../utils/api-client/api-hooks'
import { ErrorDetail } from '../../utils/error-handling/error-detail'
import { isAuthError } from '../../utils/error-utils'
import { beginSessionEndIfLapsed } from '../../utils/session-state'
import { ErrorBoundaryFallback } from './error-boundary-fallback'

const LoginRedirect: React.FC<{ redirectPath: string }> = ({ redirectPath }) => {
  // Preserve the query string so post-login lands on the exact deep link (tab, tool, drawer, etc.),
  // not just the pathname. URLSearchParams encodes the value symmetrically with the read sites.
  const params = new URLSearchParams({ 'redirect-path': redirectPath })
  return <Navigate to={`/login?${params.toString()}`} replace />
}

const AuthRedirect: React.FC = () => {
  const location = useLocation()
  const redirectPath = `${location.pathname}${location.search}`
  // A lapsed session leaves the Authentik SSO session alive, so routing to /login alone would let
  // the next Sign in through without credentials. Only the server's signed-in cookie can tell a
  // lapse from a first visit, and ending SSO on a first visit would sign the user out of every
  // other app behind the broker (ISS-8840).
  const [hasSessionLapsed] = useState(() => beginSessionEndIfLapsed(redirectPath))

  useEffect(() => {
    if (!hasSessionLapsed) {
      return
    }

    // eslint-disable-next-line no-restricted-properties -- leaves the SPA for a server endpoint, which React Router cannot do
    window.location.assign(LOGOUT_PATH)
  }, [hasSessionLapsed])

  if (hasSessionLapsed) {
    return <p role="status">Signing you out…</p>
  }

  return <LoginRedirect redirectPath={redirectPath} />
}

type ErrorBoundaryProps = {
  children: React.ReactNode
  fallbackTitle?: string
  /** Rendered instead of the default retry fallback when the boundary catches an error. */
  fallback?: React.ReactNode
}

export const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({
  children,
  fallbackTitle = 'Failed to load data',
  fallback,
}) => (
  <QueryErrorResetBoundary>
    {({ reset }) => (
      <Sentry.ErrorBoundary
        onReset={reset}
        fallback={errorData => {
          const error = errorData.error instanceof Error ? errorData.error : new Error(String(errorData.error))

          if (isAuthError(error)) {
            return <AuthRedirect />
          }

          if (fallback !== undefined) {
            return <>{fallback}</>
          }

          const title =
            error instanceof ApiError && error.problemTitle
              ? error.problemTitle
              : error instanceof ZodParseError
                ? 'Unexpected issue'
                : fallbackTitle
          const detail = <ErrorDetail error={error} />

          return <ErrorBoundaryFallback title={title} detail={detail} onRetry={() => errorData.resetError()} />
        }}
      >
        {children}
      </Sentry.ErrorBoundary>
    )}
  </QueryErrorResetBoundary>
)
